import { QueryClient } from 'react-query';
import {
  Conversation,
  Message,
  Participant,
  User,
} from '@twilio/conversations';
import { queryKeys, QueryKeyTypes } from './queryKeys';
import {
  conversationToChatConversation,
  messageToChatMessage,
  participantToChatParticipant,
  userToChatUser,
} from './utils';

export const addConversation = (
  queryClient: QueryClient,
  userId: string,
  conversation: Conversation,
) => {
  const chatConversation = conversationToChatConversation(conversation);

  queryClient.setQueryData<QueryKeyTypes['conversation']>(
    queryKeys.conversation(userId, conversation.sid),
    chatConversation,
  );

  if (conversation.uniqueName) {
    queryClient.setQueryData<QueryKeyTypes['conversation']>(
      queryKeys.conversationName(userId, conversation.uniqueName),
      chatConversation,
    );
  }

  const oldData = queryClient.getQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
    {
      ...oldData,
      pages: [
        ...oldData.pages.slice(0, -1),
        {
          ...oldData.pages[oldData.pages.length - 1],
          items: [
            ...oldData.pages[oldData.pages.length - 1].items,
            conversation,
          ],
        },
      ],
    },
  );
};

export const updateConversation = (
  queryClient: QueryClient,
  userId: string,
  conversation: Conversation,
) => {
  const chatConversation = conversationToChatConversation(conversation);

  queryClient.setQueryData<QueryKeyTypes['conversation']>(
    queryKeys.conversation(userId, conversation.sid),
    chatConversation,
  );

  if (conversation.uniqueName) {
    queryClient.setQueryData<QueryKeyTypes['conversation']>(
      queryKeys.conversationName(userId, conversation.uniqueName),
      chatConversation,
    );
  }

  const oldData = queryClient.getQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
    {
      ...oldData,
      pages: oldData.pages.map((page) => ({
        ...page,
        items: page.items.map((c) =>
          c.sid === conversation.sid ? conversation : c,
        ),
      })),
    },
  );
};

export const removeConversation = (
  queryClient: QueryClient,
  userId: string,
  conversation: Conversation,
) => {
  queryClient
    .invalidateQueries(queryKeys.conversation(userId, conversation.sid))
    .catch(() => {});

  if (conversation.uniqueName) {
    queryClient
      .invalidateQueries(
        queryKeys.conversationName(userId, conversation.uniqueName),
      )
      .catch(() => {});
  }

  const oldData = queryClient.getQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['conversations']>(
    queryKeys.conversations(userId),
    {
      ...oldData,
      pages: oldData.pages.map((page) => ({
        ...page,
        items: page.items.filter((c) => c.sid !== conversation.sid),
      })),
    },
  );
};

export const addMessage = async (
  queryClient: QueryClient,
  userId: string,
  message: Message,
) => {
  const chatMessage = await messageToChatMessage(message);

  queryClient.setQueryData<QueryKeyTypes['message']>(
    queryKeys.message(userId, message.conversation.sid, message.sid),
    chatMessage,
  );

  const oldData = queryClient.getQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
    {
      ...oldData,
      pages: [
        ...oldData.pages.slice(0, -1),
        {
          ...oldData.pages[oldData.pages.length - 1],
          messages: [
            ...oldData.pages[oldData.pages.length - 1].messages,
            chatMessage,
          ],
        },
      ],
    },
  );
};

export const updateMessage = async (
  queryClient: QueryClient,
  userId: string,
  message: Message,
) => {
  const chatMessage = await messageToChatMessage(message);

  queryClient.setQueryData<QueryKeyTypes['message']>(
    queryKeys.message(userId, message.conversation.sid, message.sid),
    chatMessage,
  );

  const oldData = queryClient.getQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
    {
      ...oldData,
      pages: oldData.pages.map((page) => ({
        ...page,
        messages: page.messages.map((m) =>
          m.sid === message.sid ? chatMessage : m,
        ),
      })),
    },
  );
};

export const removeMessage = (
  queryClient: QueryClient,
  userId: string,
  message: Message,
) => {
  queryClient
    .invalidateQueries(
      queryKeys.message(userId, message.conversation.sid, message.sid),
    )
    .catch(() => {});

  const oldData = queryClient.getQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['messages']>(
    queryKeys.messages(userId, message.conversation.sid),
    {
      ...oldData,
      pages: oldData.pages.map((page) => ({
        ...page,
        messages: page.messages.filter((m) => m.sid !== message.sid),
      })),
    },
  );
};

export const invalidateLastMessage = (
  queryClient: QueryClient,
  userId: string,
  conversation: Conversation,
) => {
  queryClient
    .invalidateQueries(queryKeys.lastMessage(userId, conversation.sid))
    .catch(() => {});
};

export const addParticipant = (
  queryClient: QueryClient,
  userId: string,
  participant: Participant,
) => {
  const chatParticipant = participantToChatParticipant(participant);

  queryClient.setQueryData<QueryKeyTypes['participant']>(
    queryKeys.participant(
      userId,
      participant.conversation.sid,
      participant.sid,
    ),
    chatParticipant,
  );

  const oldData = queryClient.getQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
    [...oldData, participant],
  );
};

export const updateParticipant = (
  queryClient: QueryClient,
  userId: string,
  participant: Participant,
) => {
  const chatParticipant = participantToChatParticipant(participant);

  queryClient.setQueryData<QueryKeyTypes['participant']>(
    queryKeys.participant(
      userId,
      participant.conversation.sid,
      participant.sid,
    ),
    chatParticipant,
  );

  const oldData = queryClient.getQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
    oldData.map((p) => (p.sid === participant.sid ? participant : p)),
  );
};

export const removeParticipant = (
  queryClient: QueryClient,
  userId: string,
  participant: Participant,
) => {
  queryClient
    .invalidateQueries(
      queryKeys.participant(
        userId,
        participant.conversation.sid,
        participant.sid,
      ),
    )
    .catch(() => {});

  const oldData = queryClient.getQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
  );
  if (!oldData) return;

  queryClient.setQueryData<QueryKeyTypes['participants']>(
    queryKeys.participants(userId, participant.conversation.sid),
    oldData.filter((p) => p.sid !== participant.sid),
  );
};

export const updateUser = (
  queryClient: QueryClient,
  userId: string,
  user: User,
) => {
  const chatUser = userToChatUser(user);

  queryClient.setQueryData<QueryKeyTypes['user']>(
    queryKeys.user(userId, user.identity),
    chatUser,
  );
};

export const removeUser = (
  queryClient: QueryClient,
  userId: string,
  user: User,
) => {
  queryClient
    .invalidateQueries(queryKeys.user(userId, user.identity))
    .catch(() => {});
};
